import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import {
  fetchAllCompanies,
  fetchAllCompaniesConnected,
  fetchCompany,
  fetchCompanyContactData,
  updateCompany as updateCompanyValues,
  createCompany as createNewCompany,
} from '../api/company.api'
import { CompanyForm, CompanyModel } from '../models/company.model'
import { isUndefined } from 'lodash'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CompanyContactData } from 'src/Libs/user/models'
import { CompanyConnectedModel } from '../models/company-connected.model'
import { Payload } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'

interface UseCompaniesHook {
  getCompany: (companyId: number) => Promise<CompanyModel | undefined>
  updateCompany: (companyId: number, values: CompanyForm) => Promise<void>
  createCompany: (company: CompanyForm) => Promise<number | null>
  getCompanies: () => Promise<CompanyModel[]>
  getCompaniesConnected: (payload?: Payload) => Promise<CompanyConnectedModel[]>
  getCompanyLocation: (id: number) => Promise<CompanyContactData | undefined>
}

/**
 * This hook provides functions the get or update a company
 */
export const useCompanies = (): UseCompaniesHook => {
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const getCompanyLocation = async (id: number): Promise<CompanyContactData | undefined> => {
    const result = await fetchCompanyContactData(id, authFetch)
    if (result.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.companyFetchError'))
      return
    }
    if (isUndefined(result.value) || !result.value) {
      enqueueErrorStack(t('service-dashboard.company.noCompany'))
      return
    }
    return result.value
  }

  const getCompany = async (companyId: number): Promise<CompanyModel | undefined> => {
    const result = await fetchCompany(companyId, authFetch)
    if (result.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.companyFetchError'))
      return
    }
    if (isUndefined(result.value) || !result.value) {
      enqueueErrorStack(t('service-dashboard.company.noCompany'))
      return
    }
    return result.value
  }

  const createCompany = async (company: CompanyForm): Promise<number | null> => {
    const result = await createNewCompany(company, authFetch)
    if (result.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.company-update-failed'))
      return null
    }

    enqueueSuccessStack(t('service-dashboard.company.company-update-success'))
    return result.value
  }

  const updateCompany = async (companyId: number, values: CompanyForm): Promise<void> => {
    const result = await updateCompanyValues(companyId, values, authFetch)
    if (result.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.company-update-failed'))
      return
    }
    enqueueSuccessStack(t('service-dashboard.company.company-update-success'))
  }

  const getCompanies = async (): Promise<CompanyModel[]> => {
    const companies = await fetchAllCompanies(authFetch)

    if (companies.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.companiesFetchError'))
      return []
    }

    if (!companies.value) return []

    return companies.value
  }

  const getCompaniesConnected = async (payload?: Payload): Promise<CompanyConnectedModel[]> => {
    const companies = await fetchAllCompaniesConnected(authFetch, payload)

    if (companies.hasErrors) {
      enqueueErrorStack(t('service-dashboard.company.companiesFetchError'))
      return []
    }

    if (!companies.value) return []

    return companies.value
  }

  return { getCompany, updateCompany, getCompanies, getCompaniesConnected, getCompanyLocation, createCompany }
}
